import type ExcelJS from 'exceljs';
import i18n from 'i18next';
import { ExportConfig } from '../components/ExportFlow';
import { Transaction, Goal } from '../types';

interface ExportData {
  transactions: Transaction[];
  goals?: Goal[];
  periodText: string;
  currency: string;
  userName?: string;
}

/**
 * Генерирует красиво оформленный Excel файл с брендингом ALVEON
 */
export async function generateExcelFile(
  data: ExportData,
  config: ExportConfig
): Promise<Blob> {
  // Lazy loading ExcelJS - загружается только когда нужен экспорт
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  
  // Метаданные
  workbook.creator = 'ALVEON';
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.lastModifiedBy = 'ALVEON';

  // Цветовая схема ALVEON
  const colors = {
    primary: 'FF667EEA',
    secondary: 'FF764BA2',
    income: 'FF34C759',
    expense: 'FFFF3B30',
    goal: 'FF007AFF',
    headerBg: 'FFF5F5F7',
    headerText: 'FF1C1C1E',
  };

  // Создаем листы
  if (config.includeExpenses || config.includeIncomes) {
    createTransactionsSheet(workbook, data, config, colors);
  }

  if (config.includeGoals && data.goals) {
    createGoalsSheet(workbook, data, config, colors);
  }

  // Создаем сводный лист (Overview)
  createOverviewSheet(workbook, data, config, colors);

  // Генерируем blob
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
}

/**
 * Создает лист с транзакциями
 */
function createTransactionsSheet(
  workbook: ExcelJS.Workbook,
  data: ExportData,
  config: ExportConfig,
  colors: any
) {
  const sheet = workbook.addWorksheet('Транзакции', {
    views: [{ state: 'frozen', xSplit: 0, ySplit: 6 }]
  });

  // Настройка колонок
  sheet.columns = [
    { width: 3 },  // Пустая колонка для отступа
    { width: 25 }, // Название
    { width: 15 }, // Сумма
    { width: 15 }, // Категория
    { width: 12 }, // Дата
    { width: 15 }, // Счёт
    { width: 12 }, // Регулярность
    { width: 30 }, // Описание
    { width: 15 }, // Баланс
  ];

  // Шапка с брендингом
  let currentRow = 1;

  // Логотип и название (объединенные ячейки)
  sheet.mergeCells(`B${currentRow}:D${currentRow}`);
  const logoCell = sheet.getCell(`B${currentRow}`);
  logoCell.value = '💎 ALVEON';
  logoCell.font = { size: 24, bold: true, color: { argb: colors.primary } };
  logoCell.alignment = { vertical: 'middle', horizontal: 'left' };
  currentRow++;

  // Подзаголовок
  sheet.mergeCells(`B${currentRow}:D${currentRow}`);
  const subtitleCell = sheet.getCell(`B${currentRow}`);
  subtitleCell.value = 'Финансовый помощник с AI';
  subtitleCell.font = { size: 11, color: { argb: 'FF8E8E93' } };
  currentRow++;

  currentRow++; // Пустая строка

  // Информация об экспорте
  sheet.mergeCells(`B${currentRow}:I${currentRow}`);
  const infoCell = sheet.getCell(`B${currentRow}`);
  infoCell.value = `📊 Экспорт транзакций • ${data.periodText}`;
  infoCell.font = { size: 14, bold: true };
  infoCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: colors.headerBg }
  };
  infoCell.alignment = { vertical: 'middle', horizontal: 'left' };
  infoCell.border = {
    top: { style: 'thin', color: { argb: 'FFE5E5EA' } },
    bottom: { style: 'thin', color: { argb: 'FFE5E5EA' } }
  };
  currentRow++;

  // Дата экспорта
  sheet.mergeCells(`B${currentRow}:I${currentRow}`);
  const dateCell = sheet.getCell(`B${currentRow}`);
  dateCell.value = `Дата выгрузки: ${new Date().toLocaleDateString('ru-RU', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}`;
  dateCell.font = { size: 10, color: { argb: 'FF8E8E93' } };
  dateCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: colors.headerBg }
  };
  dateCell.border = {
    bottom: { style: 'thin', color: { argb: 'FFE5E5EA' } }
  };
  currentRow++;

  currentRow++; // Пустая строка

  // Заголовки колонок
  const headerRow = sheet.getRow(currentRow);
  let colIndex = 2; // Начинаем с колонки B

  const headers: string[] = [];
  if (config.fields.title) headers.push('Название');
  if (config.fields.amount) headers.push('Сумма');
  if (config.fields.category) headers.push('Категория');
  if (config.fields.date) headers.push('Дата');
  if (config.fields.paymentMethod) headers.push('Счёт');
  if (config.fields.isRecurring) headers.push('Регулярность');
  if (config.fields.description) headers.push('Описание');
  if (config.fields.balance) headers.push('Баланс');

  headers.forEach(header => {
    const cell = headerRow.getCell(colIndex);
    cell.value = header;
    cell.font = { bold: true, size: 11, color: { argb: colors.headerText } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: colors.headerBg }
    };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = {
      top: { style: 'medium', color: { argb: 'FFE5E5EA' } },
      bottom: { style: 'medium', color: { argb: 'FFE5E5EA' } },
      left: { style: 'thin', color: { argb: 'FFE5E5EA' } },
      right: { style: 'thin', color: { argb: 'FFE5E5EA' } }
    };
    colIndex++;
  });
  currentRow++;

  // Фильтруем транзакции
  const filteredTransactions = data.transactions.filter(tx => {
    if (config.includeExpenses && tx.type === 'expense') return true;
    if (config.includeIncomes && tx.type === 'income') return true;
    return false;
  });

  // Данные
  filteredTransactions.forEach((tx, index) => {
    const row = sheet.getRow(currentRow);
    let colIndex = 2;

    // Чередующийся фон
    const isEven = index % 2 === 0;
    const rowFill = isEven ? 'FFFFFFFF' : 'FFF9F9F9';

    if (config.fields.title) {
      const cell = row.getCell(colIndex++);
      cell.value = tx.title;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
    }

    if (config.fields.amount) {
      const cell = row.getCell(colIndex++);
      cell.value = tx.amount;
      cell.numFmt = `#,##0.00 "${data.currency}"`;
      cell.font = { 
        color: { argb: tx.type === 'income' ? colors.income : colors.expense },
        bold: true
      };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
    }

    if (config.fields.category) {
      const cell = row.getCell(colIndex++);
      cell.value = tx.category || '-';
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
    }

    if (config.fields.date) {
      const cell = row.getCell(colIndex++);
      cell.value = new Date(tx.date);
      cell.numFmt = 'dd.mm.yyyy';
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
    }

    if (config.fields.paymentMethod) {
      const cell = row.getCell(colIndex++);
      cell.value = tx.payment_method_id || '-';
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
    }

    if (config.fields.isRecurring) {
      const cell = row.getCell(colIndex++);
      cell.value = tx.is_recurring ? 'Да' : 'Нет';
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
    }

    if (config.fields.description) {
      const cell = row.getCell(colIndex++);
      cell.value = tx.description || '-';
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
    }

    if (config.fields.balance) {
      const cell = row.getCell(colIndex++);
      cell.value = '-'; // Баланс недоступен в текущей структуре
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
    }

    currentRow++;
  });

  // Итоги
  currentRow++;
  const totalRow = sheet.getRow(currentRow);
  totalRow.getCell(2).value = 'Итого:';
  totalRow.getCell(2).font = { bold: true, size: 12 };
  totalRow.getCell(2).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: colors.headerBg }
  };

  const totalIncome = filteredTransactions
    .filter(tx => tx.type === 'income')
    .reduce((sum, tx) => sum + tx.amount, 0);
  const totalExpense = filteredTransactions
    .filter(tx => tx.type === 'expense')
    .reduce((sum, tx) => sum + tx.amount, 0);

  if (config.fields.amount) {
    totalRow.getCell(3).value = totalIncome - totalExpense;
    totalRow.getCell(3).numFmt = `#,##0.00 "${data.currency}"`;
    totalRow.getCell(3).font = { bold: true, size: 12 };
    totalRow.getCell(3).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: colors.headerBg }
    };
  }
}

/**
 * Создает лист с целями
 */
function createGoalsSheet(
  workbook: ExcelJS.Workbook,
  data: ExportData,
  config: ExportConfig,
  colors: any
) {
  const sheet = workbook.addWorksheet('Цели');
  
  // Настройка колонок
  sheet.columns = [
    { width: 3 },
    { width: 25 },
    { width: 15 },
    { width: 15 },
    { width: 12 },
  ];

  let currentRow = 1;

  // Заголовок
  sheet.mergeCells(`B${currentRow}:E${currentRow}`);
  const titleCell = sheet.getCell(`B${currentRow}`);
  titleCell.value = '🎯 Финансовые цели';
  titleCell.font = { size: 16, bold: true, color: { argb: colors.goal } };
  currentRow += 2;

  // Заголовки
  const headerRow = sheet.getRow(currentRow);
  ['Название', 'Накоплено', 'Цель', 'Прогресс'].forEach((header, index) => {
    const cell = headerRow.getCell(index + 2);
    cell.value = header;
    cell.font = { bold: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: colors.headerBg }
    };
  });
  currentRow++;

  // Данные целей
  data.goals?.forEach(goal => {
    const row = sheet.getRow(currentRow);
    row.getCell(2).value = goal.name;
    row.getCell(3).value = goal.current_amount;
    row.getCell(3).numFmt = `#,##0.00 "${data.currency}"`;
    row.getCell(4).value = goal.target_amount || 0;
    row.getCell(4).numFmt = `#,##0.00 "${data.currency}"`;
    
    const progress = goal.target_amount 
      ? (goal.current_amount / goal.target_amount) * 100 
      : 0;
    row.getCell(5).value = progress / 100;
    row.getCell(5).numFmt = '0.0%';
    
    currentRow++;
  });
}

/**
 * Создает сводный лист
 */
function createOverviewSheet(
  workbook: ExcelJS.Workbook,
  data: ExportData,
  config: ExportConfig,
  colors: any
) {
  const sheet = workbook.addWorksheet('Сводка');
  sheet.columns = [{ width: 3 }, { width: 30 }, { width: 20 }];

  let currentRow = 1;

  // Логотип
  sheet.mergeCells(`B${currentRow}:C${currentRow}`);
  const logoCell = sheet.getCell(`B${currentRow}`);
  logoCell.value = '💎 ALVEON Financial Report';
  logoCell.font = { size: 20, bold: true, color: { argb: colors.primary } };
  currentRow += 3;

  // Период
  sheet.getCell(`B${currentRow}`).value = 'Период:';
  sheet.getCell(`B${currentRow}`).font = { bold: true };
  sheet.getCell(`C${currentRow}`).value = data.periodText;
  currentRow += 2;

  // Статистика
  const totalIncome = data.transactions
    .filter(tx => tx.type === 'income')
    .reduce((sum, tx) => sum + tx.amount, 0);
  const totalExpense = data.transactions
    .filter(tx => tx.type === 'expense')
    .reduce((sum, tx) => sum + tx.amount, 0);

  sheet.getCell(`B${currentRow}`).value = 'Всего доходов:';
  sheet.getCell(`C${currentRow}`).value = totalIncome;
  sheet.getCell(`C${currentRow}`).numFmt = `#,##0.00 "${data.currency}"`;
  sheet.getCell(`C${currentRow}`).font = { color: { argb: colors.income }, bold: true };
  currentRow++;

  sheet.getCell(`B${currentRow}`).value = 'Всего расходов:';
  sheet.getCell(`C${currentRow}`).value = totalExpense;
  sheet.getCell(`C${currentRow}`).numFmt = `#,##0.00 "${data.currency}"`;
  sheet.getCell(`C${currentRow}`).font = { color: { argb: colors.expense }, bold: true };
  currentRow++;

  sheet.getCell(`B${currentRow}`).value = 'Баланс:';
  sheet.getCell(`C${currentRow}`).value = totalIncome - totalExpense;
  sheet.getCell(`C${currentRow}`).numFmt = `#,##0.00 "${data.currency}"`;
  sheet.getCell(`C${currentRow}`).font = { bold: true, size: 14 };
  currentRow += 3;

  // Футер
  sheet.mergeCells(`B${currentRow}:C${currentRow}`);
  const footerCell = sheet.getCell(`B${currentRow}`);
  footerCell.value = '✨ Сделано в ALVEON';
  footerCell.font = { size: 11, italic: true, color: { argb: 'FF8E8E93' } };
  footerCell.alignment = { horizontal: 'center' };
}

/**
 * Скачивает Excel файл с учетом особенностей Telegram WebApp и мобильных устройств
 */
export function downloadExcelFile(blob: Blob, filename: string): { needsIOSInstructions: boolean; popupShown: boolean } {
  const tg = window.Telegram?.WebApp;
  
  // В Telegram WebApp всегда используем window.open для надежности
  if (tg) {
    const result = downloadFileForTelegram(blob, filename, tg);
    return result;
  } else {
    // Для обычного браузера используем стандартный метод
    downloadFileDefault(blob, filename);
    return { needsIOSInstructions: false, popupShown: false };
  }
}

/**
 * Метод скачивания для Telegram WebApp (все платформы)
 * ВАЖНО: В Telegram Mini Apps window.open часто блокируется!
 * Для надежного скачивания используйте downloadFileFromUrl с tg.openLink()
 */
function downloadFileForTelegram(blob: Blob, filename: string, tg: any): { needsIOSInstructions: boolean; popupShown: boolean } {
  console.warn('⚠️ Скачивание через blob URL может не работать в Telegram WebView');
  console.warn('💡 Рекомендация: используйте tg.openLink() с прямой ссылкой на файл');
  
  try {
    // Создаем URL для blob
    const url = URL.createObjectURL(blob);
    
    // Пытаемся открыть файл в новой вкладке
    const newWindow = window.open(url, '_blank');
    
    if (!newWindow) {
      // WebView заблокировал всплывающее окно - используем fallback
      console.error('❌ window.open заблокирован в Telegram WebView');
      
      // Пробуем стандартный метод с download ссылкой
      downloadFileDefault(blob, filename);
      
      return { needsIOSInstructions: false, popupShown: false };
    }
    
    // Очищаем URL через 30 секунд (даем время на скачивание)
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 30000);
    
    // Если окно открылось - возможно сработает
    return { needsIOSInstructions: false, popupShown: false };
    
  } catch (error) {
    console.error('❌ Ошибка при скачивании через blob:', error);
    // Fallback: пробуем стандартный метод
    downloadFileDefault(blob, filename);
    return { needsIOSInstructions: false, popupShown: false };
  }
}

/**
 * Скачивание файла через прямую ссылку (рекомендуемый метод для Telegram Mini Apps)
 * Использует tg.openLink() который открывает URL во внешнем браузере
 */
export function downloadFileFromUrl(url: string, tg: any): void {
  if (!tg || !tg.openLink) {
    console.error('❌ Telegram WebApp API недоступен');
    // Fallback для обычного браузера
    window.open(url, '_blank');
    return;
  }
  
  console.log('📥 Открываем файл через внешний браузер:', url);
  
  // Открываем URL во внешнем браузере устройства
  // Это работает надежно на всех платформах (iOS, Android, Desktop)
  tg.openLink(url);
  
  // Показываем уведомление
  try {
    tg.showPopup({
      title: '📥 Скачивание начато',
      message: 'Файл откроется во внешнем браузере.\n\nСкачайте его оттуда в удобное место.',
      buttons: [{ id: 'ok', type: 'default', text: 'Понятно' }]
    });
  } catch (error) {
    console.error('Не удалось показать popup:', error);
  }
}

/**
 * Стандартный метод скачивания файла с улучшениями
 */
function downloadFileDefault(blob: Blob, filename: string) {
  try {
    const navigatorAny = window.navigator as any;
    if (navigatorAny?.msSaveOrOpenBlob) {
      navigatorAny.msSaveOrOpenBlob(blob, filename);
      return;
    }

    const isSafari = /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
    if (isSafari) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const url = reader.result as string;
        const popup = window.open(url, '_blank');
        if (!popup) {
          alert(i18n.t('export.allowPopup'));
        }
      };
      reader.readAsDataURL(blob);
      return;
    }

    // Создаем URL для blob
    const url = URL.createObjectURL(blob);
    
    // Создаем временную ссылку
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Добавляем ссылку в DOM (необходимо для некоторых браузеров)
    document.body.appendChild(link);
    
    // Программно кликаем по ссылке
    link.click();
    
    // Небольшая задержка перед удалением ссылки и очисткой URL
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    
    // Показываем уведомление об успехе (если доступно)
    const tg = window.Telegram?.WebApp;
    if (tg && tg.HapticFeedback) {
      tg.HapticFeedback.notificationOccurred('success');
    }
  } catch (error) {
    
    // Показываем ошибку пользователю
    const tg = window.Telegram?.WebApp;
    if (tg) {
      try {
        tg.showAlert(i18n.t('export.saveFileFailed'));
      } catch (popupError: any) {
        // Игнорируем ошибку если popup уже открыт
        console.error('Cannot show alert:', popupError);
      }
    } else {
      alert(i18n.t('export.saveFileFailed'));
    }
  }
}
